/**
 * Pure-data English catalogue for namespace 'tools' (W2-11 batch-tools; imports only core/util/types).
 * Failure reasons and toast notes of the batch tools (Distribute, Clone to lines, Fill between, Motion path,
 * Flatten, Fit, Stagger). Keys are written WITHOUT the namespace prefix; the tools produce MessageRefs such as
 * 'tools.fill.sameLine' (constants in common.ts `TOOLS_MSG`).
 */

import type { MessageCatalog } from '../util/types';

const catalog: MessageCatalog = {
  ns: 'tools',
  locale: 'en',
  messages: {
    // Distribute / Ramp
    'distribute.nothing': 'Select the notes or keyframes to distribute values over.',
    'distribute.badNumber': 'From, To, the pattern and the random range must be numbers.',
    'distribute.notNumeric': 'These keyframes hold colours or texts; Distribute works on numeric values only.',
    'distribute.lastKey':
      '{count, plural, one {# last keyframe was} other {# last keyframes were}} skipped: a curve belongs to the segment after a keyframe.',
    'distribute.easeSkipped':
      '{count, plural, one {# segment keeps its curve} other {# segments keep their curves}}: that curve is not allowed there.',
    'distribute.clamped':
      '{count, plural, one {# value was} other {# values were}} outside the allowed range and set to the nearest allowed value.',
    'distribute.evalFailed': 'Item {index} of {count} got no usable value. Check From, To and the pattern.',
    'distribute.arriveAfterStay':
      '{count, plural, one {# arrive value was} other {# arrive values were}} kept: after a Stay the arrive value is the stayed value.',
    'distribute.skippedKeys':
      '{count, plural, one {# keyframe holds} other {# keyframes hold}} a colour or a text and {count, plural, one {was} other {were}} left unchanged.',

    // Clone to lines
    'clone.nothing': 'Select the segments (and notes) to clone first.',
    'clone.oneLine': 'Clone works on the motion and notes of one line at a time. Select items of a single line.',
    'clone.noTargets': 'Choose at least one target line, or create new lines.',
    'clone.count': 'Create between 1 and {max} lines.',
    'clone.skippedOverlap':
      '{count, plural, one {# copy was} other {# copies were}} skipped because the target already moves there.',
    'clone.notesOnUiMover':
      'Notes were not copied to {count, plural, one {# UI mover line} other {# UI mover lines}}: Phira does not draw notes on them.',
    'clone.notApplicable':
      'The {prop, select, moveX {X} moveY {Y} rotate {rotation} alpha {opacity} other {property}} ramp was not used: no {prop, select, moveX {X} moveY {Y} rotate {rotation} alpha {opacity} other {such}} segments are cloned.',
    'clone.newLayerFallback':
      '{count, plural, one {# copy} other {# copies}} replaced the existing motion: no new layer was possible there (the property has no layers, or all four are used).',
    'clone.sourceRemoved': 'The source motion was removed (Keep source is off): it now stays at its start value.',
    'clone.targetSkipped':
      '{count, plural, one {# target line no longer exists and was} other {# target lines no longer exist and were}} skipped.',

    // Fill between
    'fill.needTwo': 'Select two notes: where the fill starts and where it ends.',
    'fill.sameLine': 'Both notes must be on the same line.',
    'fill.sameTime': 'The two notes are at the same time. Choose notes at different times.',
    'fill.badSpacing': 'The spacing must be longer than zero, or a count of at least 1.',
    'fill.tooMany': 'That would create more than {max} notes. Use a wider spacing.',
    'fill.none': 'No note fits between the two notes at this spacing. Use a finer spacing.',
    'fill.template': 'The new-note defaults are not available. Use the start note’s properties instead.',

    // Motion path
    'path.range': 'The path needs a start before its end, at or after beat 0.',
    'path.expr': 'The {axis, select, x {x(t)} y {y(t)} r {r(t)} other {path}} expression has an error.',
    'path.eval': 'The path has no value at beat {beat} (for example a division by zero). Adjust the expressions.',
    'path.density': 'Keyframes per beat must be a whole number from 1 to {max}.',
    'path.tolerance': 'The fit tolerance must be a number above 0.',
    'path.tooMany': 'The path would need more than {max} keyframes per axis. Use fewer keyframes per beat or a larger tolerance.',
    'path.overlap':
      'The line already moves on this layer within the path’s time. Choose Replace, or write to a new layer.',
    'path.noFreeLayer': 'All four layers of this line are in use. Choose a layer to replace.',
    'path.layer': 'The path was written to layer {layer}.',
    'path.layerInvalid': 'Layers are numbered from 0.',
    'path.fitted': 'Fitted with {count} keyframes within {tolerance} px.',

    // Flatten
    'flatten.notLayered': 'Only position, rotation, opacity and scroll speed have layers to flatten.',
    'flatten.oneLayer': 'This property has a single layer: there is nothing to flatten.',
    'flatten.tolerance': 'The tolerance must be a number above 0.',
    'flatten.exact': 'Layers flattened exactly: the motion is unchanged.',
    'flatten.baked':
      'Layers flattened. {count, plural, one {# stretch} other {# stretches}} could not be combined exactly and became short straight pieces (largest difference {error}).',
    'flatten.tooMany': 'Flattening would need more than {max} keyframes. Use a larger tolerance.',
    'flatten.partner': 'Position X and Y are linked on this line, so both were flattened together.',

    // Fit
    'fit.tolerance': 'The tolerance must be a number above 0.',
    'fit.nothing': 'Select numeric segments to simplify.',
    'fit.done': 'Simplified from {before} to {after} keyframes (largest difference {error}).',
    'fit.none': 'No keyframes could be merged within a tolerance of {tolerance}.',

    // Stagger and re-timing
    'stagger.nothing': 'Select the notes or keyframes to stagger.',
    'stagger.zero': 'The stagger step must not be zero.',
    'stagger.single': 'Stagger needs items at two or more different times.',
    'retime.collide':
      'Keyframes would pass or land on their neighbours. Use a smaller step or select the neighbours too.',
    'retime.beforeStart': 'Items cannot move before beat 0.',

    // Shared
    lineMissing: 'This line no longer exists.',
  },
};

export default catalog;
